import { MessageBox, MessageBoxButton, MessageBoxButtons } from '@bimser/common';
import { BCLikert, BCNotification, IDefinition, IOption, findMessage } from '@bimser/components';
import { FormItemTypes, IDictionary, MLHelper, createGuid } from '@bimser/core';
import { IPropertyField } from '../../../../entities/common';
import DataBoundControl from '../../base/DataBoundControl';
import {
	ILikertOnAddedDefinitionEventArgs,
	ILikertOnAddedOptionEventArgs,
	ILikertOnAddingDefinitionEventArgs,
	ILikertOnAddingOptionEventArgs,
	ILikertOnDeletedDefinitionEventArgs,
	ILikertOnDeletedOptionEventArgs,
	ILikertOnDeletingDefinitionEventArgs,
	ILikertOnDeletingOptionEventArgs,
	ILikertOnEditedDefinitionEventArgs,
	ILikertOnEditedOptionEventArgs,
	ILikertOnEditingDefinitionEventArgs,
	ILikertOnEditingOptionEventArgs
} from '../../entities/EventArgs';
import { TitleModal } from './components';
import { ILikertProperties } from './entities';

export default class Likert extends DataBoundControl {
	hideAddOption: boolean;
	hideAddDefinition: boolean;
	hideDeleteOption: boolean;
	hideDeleteDefinition: boolean;
	hideEditOption: boolean;
	hideEditDefinition: boolean;
	minOptionsCount: number;
	minDefinitionsCount: number;
	maxOptionsCount: number;
	maxDefinitionsCount: number;
	options: IOption[];
	definitions: IDefinition[];

	constructor() {
		super();

		this.controlType = FormItemTypes.EControlTypes.Likert;

		this.hideAddOption = false;
		this.hideAddDefinition = false;
		this.hideDeleteOption = false;
		this.hideDeleteDefinition = false;
		this.hideEditOption = false;
		this.hideEditDefinition = false;
		this.minOptionsCount = 1;
		this.minDefinitionsCount = 1;
		this.maxOptionsCount = 7;
		this.maxDefinitionsCount = 100;
		this.options = [];
		this.definitions = [];

		this.onNewDefinitionAdded = this.onNewDefinitionAdded.bind(this);
		this.onDefinitionUpdated = this.onDefinitionUpdated.bind(this);
		this.onDefinitionDeleted = this.onDefinitionDeleted.bind(this);
		this.onNewOptionAdded = this.onNewOptionAdded.bind(this);
		this.onOptionDeleted = this.onOptionDeleted.bind(this);
		this.onOptionUpdated = this.onOptionUpdated.bind(this);
		this.showAddTitleModal = this.showAddTitleModal.bind(this);
		this.showEditTitleModal = this.showEditTitleModal.bind(this);
		this.showConfirmModal = this.showConfirmModal.bind(this);
	}

	__loadProperties(data: ILikertProperties) {
		super.__loadProperties(data);

		this.__proxyChanged.hideAddOption = data.hideAddOption;
		this.__proxyChanged.hideAddDefinition = data.hideAddDefinition;
		this.__proxyChanged.hideDeleteOption = data.hideDeleteOption;
		this.__proxyChanged.hideDeleteDefinition = data.hideDeleteDefinition;
		this.__proxyChanged.hideEditOption = data.hideEditOption;
		this.__proxyChanged.hideEditDefinition = data.hideEditDefinition;
		this.__proxyChanged.minOptionsCount = data.minOptionsCount;
		this.__proxyChanged.minDefinitionsCount = data.minDefinitionsCount;
		this.__proxyChanged.maxOptionsCount = data.maxOptionsCount;
		this.__proxyChanged.maxDefinitionsCount = data.maxDefinitionsCount;
		this.__proxyChanged.options = data.options;
		this.__proxyChanged.definitions = data.definitions;
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			hideAddOption: this.hideAddOption,
			hideAddDefinition: this.hideAddDefinition,
			hideDeleteOption: this.hideDeleteOption,
			hideDeleteDefinition: this.hideDeleteDefinition,
			hideEditOption: this.hideEditOption,
			hideEditDefinition: this.hideEditDefinition,
			minOptionsCount: this.minOptionsCount,
			minDefinitionsCount: this.minDefinitionsCount,
			maxOptionsCount: this.maxOptionsCount,
			maxDefinitionsCount: this.maxDefinitionsCount,
			options: this.options,
			definitions: this.definitions
		};
	}

	__getPropertyFields(): IPropertyField[] {
		return [
			...super.__getPropertyFields(),
			{
				name: 'hideAddOption',
				type: 'boolean'
			},
			{
				name: 'hideAddDefinition',
				type: 'boolean'
			},
			{
				name: 'hideDeleteOption',
				type: 'boolean'
			},
			{
				name: 'hideDeleteDefinition',
				type: 'boolean'
			},
			{
				name: 'hideEditOption',
				type: 'boolean'
			},
			{
				name: 'hideEditDefinition',
				type: 'boolean'
			},
			{
				name: 'minOptionsCount',
				type: 'number'
			},
			{
				name: 'minDefinitionsCount',
				type: 'number'
			},
			{
				name: 'maxOptionsCount',
				type: 'string'
			},
			{
				name: 'maxDefinitionsCount',
				type: 'number'
			},
			{
				name: 'options',
				type: 'array'
			},
			{
				name: 'definitions',
				type: 'array'
			}
		];
	}

	async showConfirmModal() {
		const result = await MessageBox.confirm({
			content: findMessage.get('100653'),
			buttons: MessageBoxButtons.YesNo,
			notInCloseIcon: true,
			width: 'small'
		});

		return result.button.id === MessageBoxButton.Yes;
	}

	async showAddTitleModal(type: 'option' | 'definition') {
		let newTitle: IDictionary<string> = {};

		return new Promise<IDictionary<string> | void>(resolve =>
			MessageBox.show({
				// TODO: Interaction Manager ile render edilmeli. Yoksa SDK ile style kayıplarına yol açacaktır.
				id: TitleModal.name,
				title: findMessage.get('100016'),
				width: 'small',
				notInCloseIcon: true,
				content: (
					<TitleModal
						maxLength={type === 'option' ? 255 : 500}
						title={newTitle}
						onChange={title => {
							newTitle = title;
						}}
					/>
				),
				buttons: [
					{
						id: MessageBoxButton.OK,
						disableAutoClose: true,
						type: 'primary',
						text: findMessage.get('100004'),
						callback() {
							if (MLHelper.isMLEmpty(newTitle)) {
								return BCNotification.warning({
									message: findMessage
										.get('101632')
										.replace('{0}', findMessage.get('100029'))
								});
							}

							resolve(newTitle);
							MessageBox.closeModal(TitleModal.name);
						}
					},
					{
						id: MessageBoxButton.Cancel,
						text: findMessage.get('100034'),
						callback() {
							MessageBox.closeModal(TitleModal.name);
						}
					}
				]
			})
		);
	}

	async showEditTitleModal(title: IDictionary<string>, type: 'option' | 'definition') {
		let editedTitle = { ...title };

		return new Promise<IDictionary<string> | void>(resolve =>
			MessageBox.show({
				// TODO: Interaction Manager ile render edilmeli. Yoksa SDK ile style kayıplarına yol açacaktır.
				id: TitleModal.name,
				title: findMessage.get('101689'),
				width: 'small',
				notInCloseIcon: true,
				content: (
					<TitleModal
						maxLength={type === 'option' ? 255 : 500}
						title={editedTitle}
						onChange={title => {
							editedTitle = title;
						}}
					/>
				),
				buttons: [
					{
						id: MessageBoxButton.OK,
						disableAutoClose: true,
						type: 'primary',
						text: findMessage.get('100004'),
						callback() {
							if (MLHelper.isMLEmpty(editedTitle)) {
								return BCNotification.warning({
									message: findMessage
										.get('101632')
										.replace('{0}', findMessage.get('100029'))
								});
							}

							resolve(editedTitle);
							MessageBox.closeModal(TitleModal.name);
						}
					},
					{
						id: MessageBoxButton.Cancel,
						text: findMessage.get('100034'),
						callback() {
							MessageBox.closeModal(TitleModal.name);
						}
					}
				]
			})
		);
	}

	async onNewDefinitionAdded(newDefinitions: IDefinition[], definition: IDefinition) {
		const onAddingDefinitionEventArgs: ILikertOnAddingDefinitionEventArgs = {
			cancel: false,
			definition
		};

		await this.emit('event:OnAddingDefinition', onAddingDefinitionEventArgs);
		const [args] = await this.emit(
			'event:server:OnAddingDefinition',
			onAddingDefinitionEventArgs
		);

		if (onAddingDefinitionEventArgs.cancel || args?.executedServerEvent?.parameters.cancel) {
			return;
		}

		this.__proxyChanging.definitions = newDefinitions;

		const onAddedDefinitionEventArgs: ILikertOnAddedDefinitionEventArgs = {
			newDefinitions,
			definition
		};

		await this.emit('event:OnAddedDefinition', onAddedDefinitionEventArgs);
		await this.emit('event:server:OnAddedDefinition', onAddedDefinitionEventArgs);
	}

	async onDefinitionDeleted(newDefinitions: IDefinition[], definition: IDefinition) {
		const onDeletingDefinitionEventArgs: ILikertOnDeletingDefinitionEventArgs = {
			cancel: false,
			definition
		};

		await this.emit('event:OnDeletingDefinition', onDeletingDefinitionEventArgs);
		const [args] = await this.emit(
			'event:server:OnDeletingDefinition',
			onDeletingDefinitionEventArgs
		);

		if (onDeletingDefinitionEventArgs.cancel || args?.executedServerEvent?.parameters.cancel) {
			return;
		}

		this.__proxyChanging.definitions = newDefinitions;

		const onDeletedDefinitionEventArgs: ILikertOnDeletedDefinitionEventArgs = {
			newDefinitions,
			definition
		};

		await this.emit('event:OnDeletedDefinition', onDeletedDefinitionEventArgs);
		await this.emit('event:server:OnDeletedDefinition', onDeletedDefinitionEventArgs);
	}

	async onDefinitionUpdated(
		newDefinitions: IDefinition[],
		newDefinition: IDefinition,
		oldDefinition: IDefinition
	) {
		const onEditingDefinitionEventArgs: ILikertOnEditingDefinitionEventArgs = {
			cancel: false,
			newDefinition,
			oldDefinition
		};

		await this.emit('event:OnEditingDefinition', onEditingDefinitionEventArgs);
		const [args] = await this.emit(
			'event:server:OnEditingDefinition',
			onEditingDefinitionEventArgs
		);

		if (onEditingDefinitionEventArgs.cancel || args?.executedServerEvent?.parameters.cancel) {
			return;
		}

		this.__proxyChanging.definitions = newDefinitions;

		const onEditedDefinitionEventArgs: ILikertOnEditedDefinitionEventArgs = {
			newDefinitions,
			newDefinition,
			oldDefinition
		};

		await this.emit('event:OnEditedDefinition', onEditedDefinitionEventArgs);
		await this.emit('event:server:OnEditedDefinition', onEditedDefinitionEventArgs);
	}

	async onNewOptionAdded(newOptions: IOption[], option: IOption) {
		const onAddingOptionEventArgs: ILikertOnAddingOptionEventArgs = {
			cancel: false,
			option
		};

		await this.emit('event:OnAddingOption', onAddingOptionEventArgs);
		const [args] = await this.emit('event:server:OnAddingOption', onAddingOptionEventArgs);

		if (onAddingOptionEventArgs.cancel || args?.executedServerEvent?.parameters.cancel) {
			return;
		}

		this.__proxyChanging.options = newOptions;

		const onAddedOptionEventArgs: ILikertOnAddedOptionEventArgs = {
			newOptions,
			option
		};

		await this.emit('event:OnAddedOption', onAddedOptionEventArgs);
		await this.emit('event:server:OnAddedOption', onAddedOptionEventArgs);
	}

	async onOptionDeleted(newOptions: IOption[], newDefinitions: IDefinition[], option: IOption) {
		const onDeletingOptionEventArgs: ILikertOnDeletingOptionEventArgs = {
			cancel: false,
			option
		};

		await this.emit('event:OnDeletingOption', onDeletingOptionEventArgs);
		const [args] = await this.emit('event:server:OnDeletingOption', onDeletingOptionEventArgs);

		if (onDeletingOptionEventArgs.cancel || args?.executedServerEvent?.parameters.cancel) {
			return;
		}

		this.__proxyChanging.options = newOptions;
		this.__proxyChanging.definitions = newDefinitions;

		const onDeletedOptionEventArgs: ILikertOnDeletedOptionEventArgs = {
			newOptions,
			newDefinitions,
			option
		};

		await this.emit('event:OnDeletedOption', onDeletedOptionEventArgs);
		await this.emit('event:server:OnDeletedOption', onDeletedOptionEventArgs);
	}

	async onOptionUpdated(newOptions: IOption[], newOption: IOption, oldOption: IOption) {
		const onEditingOptionEventArgs: ILikertOnEditingOptionEventArgs = {
			cancel: false,
			newOption,
			oldOption
		};

		await this.emit('event:OnEditingOption', onEditingOptionEventArgs);
		const [args] = await this.emit('event:server:OnEditingOption', onEditingOptionEventArgs);

		if (onEditingOptionEventArgs.cancel || args?.executedServerEvent?.parameters.cancel) {
			return;
		}

		this.__proxyChanging.options = newOptions;

		const onEditedOptionEventArgs: ILikertOnEditedOptionEventArgs = {
			newOptions,
			newOption,
			oldOption
		};

		await this.emit('event:OnEditedOption', onEditedOptionEventArgs);
		await this.emit('event:server:OnEditedOption', onEditedOptionEventArgs);
	}

	__render() {
		return super.__render(
			<BCLikert
				name={`Likert_${this.name}`}
				definitions={this.definitions}
				options={this.options}
				hideAddDefinition={this.hideAddDefinition}
				hideAddOption={this.hideAddOption}
				hideDeleteDefinition={this.hideDeleteDefinition}
				hideDeleteOption={this.hideDeleteOption}
				hideEditDefinition={this.hideEditDefinition}
				hideEditOption={this.hideEditOption}
				maxDefinitionsCount={this.maxDefinitionsCount}
				maxOptionsCount={this.maxOptionsCount}
				readonly={this.__isReadOnly()}
				disabled={this.__isDisabled()}
				onNewDefinitionAdded={this.onNewDefinitionAdded}
				onDefinitionUpdated={this.onDefinitionUpdated}
				onDefinitionDeleted={this.onDefinitionDeleted}
				onNewOptionAdded={this.onNewOptionAdded}
				onOptionDeleted={this.onOptionDeleted}
				onOptionUpdated={this.onOptionUpdated}
				showAddTitleModal={this.showAddTitleModal}
				showEditTitleModal={this.showEditTitleModal}
				showConfirmModal={this.showConfirmModal}
			/>
		);
	}
}
